/******************************************************************************************
 * File: apiService.js
 *
 * Purpose:
 * Centralized API client responsible for:
 *  - Environment-based API URL resolution
 *  - Secure authenticated HTTP requests
 *  - Timeout handling
 *  - Standardized error handling
 *
 * Design Principles:
 *  - Single Responsibility
 *  - DRY (Do Not Repeat Yourself)
 *  - Service-based API routing
 *  - Security-first approach
 *
 * Security Notes:
 *  - JWT token is read-only and never mutated
 *  - Unauthorized access forces logout
 *  - No sensitive data is logged
 ******************************************************************************************/

/* -----------------------------------------------------------------------------
 * Environment Configuration
 * ------------------------------------------------------------`--------------- */
const runningEnv = 'production'; // production | uat | dev | local | local_wifi
export const URLS = getURLs(runningEnv);

export function getURLs(runningEnv) {
    const env = runningEnv || process.env.REACT_APP_ENV || "local"; // production | uat | dev | local | local_wifi

    switch (env) {
        case "production":
            return buildURLs("https://gateway.doql.com", env);

        case "test":
            return buildURLs("https://test.doql.com", env);

        case "uat":
            return buildURLs("https://uat-gateway.doql.com", env);

        case "dev":
            return buildURLs("https://dev-gateway.doql.com", env);

        case "local_wifi":
            return buildURLs("http://192.168.31.54:8000", env);
        // return buildURLs("https://4493-2409-40d4-30c0-3e7-d8ae-f2d9-62e8-b94e.ngrok-free.app", env);

        case "local":
        default:
            // return buildURLs("http://local-gateway.doql.com:8080", env); // fallback
            return buildURLs("http://localhost:8000", env);
    }
}

/* -----------------------------------------------------------------------------
/**
 * Resolves API base URLs based on environment
 *
 * @param {string} type - Environment type
 * @returns {Object<string, string>} Base URLs per service
 */
export function buildURLs(API_GATEWAY, env) {
    const isAwsEnv = ["production", "uat", "dev"].includes(env);
    const socketBase = isAwsEnv ? API_GATEWAY : API_GATEWAY.replace(":8080", ":8000");

    return {
        user: `${API_GATEWAY}/api-user`,
        catalog: `${API_GATEWAY}/api-catalog`,
        billing: `${API_GATEWAY}/api-order`,
        notification: `${API_GATEWAY}/api-notification`,
        notification_socket: socketBase,
        logging: `${API_GATEWAY}/api-logging`,
    };
}

/* -----------------------------------------------------------------------------
 * Authentication Helpers
 * --------------------------------------------------------------------------- */
/**
 * Retrieves JWT auth token from local storage
 *
 * @returns {string|null}
 */
const getAuthToken = () => localStorage.getItem("authToken");

/**
 * Clears auth data and redirects user to login page
 *
 * Security:
 * Ensures token is removed immediately on unauthorized access
 */
const clearAuthAndRedirect = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    localStorage.removeItem("permissions");
    localStorage.removeItem("permissionsLoaded");
    // window.location.href = "/login";
};

/* -----------------------------------------------------------------------------
 * Refresh-Token Recovery
 * ---------------------------------------------------------------------------
 * Single-flight refresh: when multiple concurrent requests all hit a 401,
 * only one POST /refresh fires and every caller awaits the same promise.
 *
 * Response shape: { token: "<new-jwt>" }
 *
 * On success → new token is persisted to localStorage and returned to the
 * caller, which retries the original request once with the new credential.
 * On failure → returns null; the caller logs the user out.
 * --------------------------------------------------------------------------- */
let refreshPromise = null;

const refreshAccessToken = () => {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
        const currentToken = getAuthToken();
        if (!currentToken) return null;

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);

        try {
            const res = await fetch(`${URLS.user}/refresh`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${currentToken}`,
                },
                body: JSON.stringify({}),
                signal: controller.signal,
            });

            if (!res.ok) return null;
            const data = await res.json();
            if (!data?.token) return null;

            localStorage.setItem("authToken", data.token);
            return data.token;
        } catch (err) {
            return null;
        } finally {
            clearTimeout(timeoutId);
        }
    })();

    /* Reset so a future expiry can trigger a fresh refresh attempt. */
    refreshPromise.finally(() => {
        refreshPromise = null;
    });

    return refreshPromise;
};

/* -----------------------------------------------------------------------------
 * Core API Request Handler
 * --------------------------------------------------------------------------- */
/**
 * Generic API request handler
 *
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {string} service - Service key (user | catalog | billing | notification | logging)
 * @param {string} endpoint - API endpoint (example: "/login")
 * @param {Object|null} payload - Request body (POST/PUT only)
 * @param {Object} config - Optional fetch configuration
 *
 * @returns {Promise<Object|null>} Parsed JSON response or null
 *
 * @throws {Error} Validation errors, network errors, API errors
 *
 * Request Payload Example:
 * {
 *   "email": "user@example.com",
 *   "password": "******"
 * }
 *
 * Response Payload Example:
 * {
 *   "success": true,
 *   "data": { "id": 1, "name": "John" }
 * }
 */
const callApi = async (
    method,
    service,
    endpoint,
    payload = null,
    config = {}
) => {

    /* -----------------------------
     * Mandatory Field Validation
     * --------------------------- */
    if (!method) throw new Error("HTTP method is required");
    if (!service) throw new Error("API service is required");
    if (!endpoint) throw new Error("API endpoint is required");

    const baseURL = URLS[service];
    if (!baseURL) {
        throw new Error(`Invalid API service: ${service}`);
    }

    /* -----------------------------
     * Request Timeout Handling
     * --------------------------- */
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 150000);
    const token = getAuthToken();

    /* -----------------------------
     * Fetch Configuration
     * --------------------------- */
    // const fetchConfig = {
    //     method: method.toUpperCase(),
    //     headers: {
    //         "Content-Type": "application/json",
    //         ...(token && {Authorization: `Bearer ${token}`}),
    //         ...(config.headers || {}),
    //     },
    //     signal: controller.signal,
    //     ...config,
    // };
    const isFormData = payload instanceof FormData;

    const fetchConfig = {
        method: method.toUpperCase(),
        headers: {
            // ...(isFormData ? {} : !payload && !isFormData ? {} : { "Content-Type": "application/json" }),
            ...(payload && !isFormData ? { "Content-Type": "application/json" } : {}),
            ...(token && { Authorization: `Bearer ${token}` }),
            ...(config.headers || {}),
        },
        signal: controller.signal,
        ...config,
    };

    if (payload) {
        fetchConfig.body = isFormData ? payload : JSON.stringify(payload);
    }


    try {
        const response = await fetch(`${baseURL}${endpoint}`, fetchConfig);
        clearTimeout(timeoutId);

        /* -----------------------------
         * Authorization Handling
         * ---------------------------
         * 401 → token may have expired. Try POST /refresh once and replay
         *       the original request with the new token. Only if the
         *       refresh fails do we clear auth + redirect.
         * 403 → server rejected the principal; a refresh wouldn't help, so
         *       fall straight through to logout.
         * --------------------------- */
        if (
            response.status === 401 &&
            !config.skipAuthRedirect &&
            !config._refreshAttempted
        ) {
            const newToken = await refreshAccessToken();
            if (newToken) {
                return callApi(method, service, endpoint, payload, {
                    ...config,
                    _refreshAttempted: true,
                });
            }
        }

        if (response.status === 401 || response.status === 403) {
            if (!config.skipAuthRedirect) {
                clearAuthAndRedirect();
                window.location.replace("/login");
            }
        }

        /* -----------------------------
         * Error Handling
         * --------------------------- */
        if (!response.ok) {
            const errorData = await response.json(); // backend JSON
            throw new Error(JSON.stringify(errorData)); // 👈 PURE JSON throw
        }

        /* -----------------------------
         * No Content Response
         * --------------------------- */
        if (response.status === 204) {
            return null;
        }

        /* -----------------------------
         * Raw Response (file downloads)
         * ---------------------------
         * With config.rawResponse the caller gets the Response object and
         * handles blob()/text() itself (e.g. CSV exports).
         * --------------------------- */
        if (config.rawResponse) {
            return response;
        }

        return await response.json();
    } catch (error) {
        if (error.name === "AbortError") {
            throw new Error("API request timeout");
        }
        throw error;
    }
};

/* -----------------------------------------------------------------------------
 * Public API Helpers
 * --------------------------------------------------------------------------- */
/**
 * GET request
 */
export const apiGet = (service, endpoint, config = {}) =>
    callApi("GET", service, endpoint, null, config);

/**
 * POST request
 */
export const apiPost = (service, endpoint, payload, config = {}) =>
    callApi("POST", service, endpoint, payload, config);

/**
 * PUT request
 */
export const apiPut = (service, endpoint, payload, config = {}) =>
    callApi("PUT", service, endpoint, payload, config);

/**
 * PATCH request
 */
export const apiPatch = (service, endpoint, payload, config = {}) =>
    callApi("PATCH", service, endpoint, payload, config);

/**
 * DELETE request
 *
 * `payload` is optional — most deletes don't send a body, but some endpoints
 * accept one (e.g. a deletion reason).
 */
export const apiDelete = (service, endpoint, payload = null, config = {}) =>
    callApi("DELETE", service, endpoint, payload, config);
